import React from 'react';
import { noop } from '@fabric-ui/utils';
import { FormControl, InputLabel, styled, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { SeverityBGColors, SeverityIcons, severityOptions } from './Severity';

interface ItemButtonProps {
  theme?: Theme;
  error?: boolean;
  size?: 'small' | 'large';
  isSeverity?: boolean;
}

const ItemButton = styled(ToggleButton)<ItemButtonProps>(({ theme, error, size, isSeverity }) => ({
  borderColor: 'var(--color-border-severity-none-secondary)',
  ...(errorColor => ({
    borderTopColor: errorColor,
    borderBottomColor: errorColor,
    '&.MuiToggleButtonGroup-grouped:first-of-type': {
      borderLeftColor: errorColor,
    },
    '&.MuiToggleButtonGroup-grouped:last-of-type': {
      borderRightColor: errorColor,
    },
  }))(error ? 'var(--color-border-status-danger-primary)' : undefined),
  padding: '5.5px 15px',
  color: 'var(--color-content-interactive-secondary-default)',
  backgroundColor: 'var(--color-surface-base-primary)',
  fontWeight: isSeverity ? 600 : 400,
  '&:hover': {
    backgroundColor: 'var(--color-surface-interactive-modifier-disabled)',
    color: 'var(--color-content-interactive-secondary-hover)',
  },
  '&.Mui-selected': {
    backgroundColor: isSeverity ? 'transparent' : 'var(--color-surface-interactive-modifier-default)',
    color: 'var(--color-content-interactive-secondary-active)',
    '&:hover': {
      backgroundColor: isSeverity ? undefined : 'var(--color-surface-interactive-modifier-default)',
    },
  },
  svg: {
    transition: theme.transitions.create(['color'], { duration: theme.transitions.duration.shorter }),
  },
  '&:not(.Mui-selected):not(:hover) svg': {
    color: 'var(--color-surface-interactive-tertiary-hover) !important',
  },
  '& > span': {
    color: 'inherit',
    fontWeight: 'inherit',
    lineHeight: size === 'small' ? '18px' : '21px',
  },
  '&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    marginLeft: 'inherit',
    borderLeft: 'none',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'var(--color-surface-interactive-modifier-disabled)',
    color: 'var(--color-content-interactive-secondary-hover)',
  },
  '&.Mui-disabled': {
    borderColor: 'var(--color-border-severity-none-secondary)',
    color: 'var(--color-content-interactive-secondary-subdued)',
  },
}));

// Severity-specific styled button
const SeverityItemButton = styled(ItemButton)<ItemButtonProps & { severitycolor?: string }>(({ severitycolor }) => ({
  '&.Mui-selected': {
    backgroundColor: severitycolor || 'var(--color-surface-interactive-modifier-default)',
    ':hover': {
      backgroundColor: `rgb(from ${severitycolor || 'var(--color-surface-interactive-modifier-default)'} r g b / 0.7)`,
    },
  },
}));

export interface ToggleOption {
  title: string;
  value: string;
}

export interface AvToggleProps {
  label?: string;
  value?: string | number | string[];
  options?: ToggleOption[];
  onChange?: (value: any) => void;
  error?: string;
  labelColor?: string;
  multiSelect?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  size?: 'medium' | 'small' | 'smallOutline';
  getItemIcon?: (v) => string | undefined;
  isSeverity?: boolean;
}

export const AvToggle: React.FC<AvToggleProps> = ({
  label = '',
  value = '',
  onChange = noop,
  error = '',
  multiSelect = false,
  isRequired = false,
  disabled = false,
  options = [],
  size = 'medium',
  getItemIcon,
  labelColor,
  isSeverity = false,
}) => {
  const handleChange = (e, val) => {
    if (!isRequired || multiSelect || val !== null) {
      onChange(val);
    }
  };

  const ButtonComponent = isSeverity ? SeverityItemButton : ItemButton;

  return (
    <FormControl required={isRequired} error={Boolean(error)} disabled={disabled}>
      <InputLabel sx={{ '&.MuiFormLabel-root': { color: labelColor } }}>{label}</InputLabel>
      <ToggleButtonGroup value={value} exclusive={!multiSelect} onChange={handleChange} aria-label={label} disabled={disabled} size={size}>
        {options.map(({ title, value: val }) => (
          <ButtonComponent
            key={val}
            value={val}
            error={Boolean(error)}
            isSeverity={isSeverity}
            severitycolor={isSeverity && val ? SeverityBGColors[val] : undefined}
            disabled={isRequired && multiSelect && Array.isArray(value) && value.length === 1 && value.includes(val)}>
            {getItemIcon?.(val)}
            <span>{title}</span>
          </ButtonComponent>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  );
};

interface SeverityToggleProps {
  onChange: (value: string) => void;
}
export function SeverityToggle({ onChange, ...toggleProps }: SeverityToggleProps & any) {
  return (
    <AvToggle
      options={severityOptions}
      getItemIcon={val => SeverityIcons[val].icon}
      onChange={newValue =>
        onChange(toggleProps.multiSelect ? newValue.sort((a, b) => SeverityIcons[a].order - SeverityIcons[b].order) : newValue)
      }
      isSeverity={true}
      {...toggleProps}
    />
  );
}
